import { Dialog, DialogFooter, IDialogProps as FIDialogProps, IDialogStyles, Stack } from '@fluentui/react';

import { IComponent } from '../../models/components.models';
import { useThemeStyles } from '../../utils/hooks';
import { singleOrArrayToArray } from '../../utils/utils';
import { Button } from '../Button/Button';
import { buttonStylesCenter, buttonStylesGhost, buttonStylesPrimary } from '../Button/Button.styles';
import { dialogStylesDefault } from './BaseDialog.styles';

export interface IBaseDialogProps extends Omit<FIDialogProps, 'styles'>, IComponent<IDialogStyles> {
  title?: string;
  msg?: string;
  content?: JSX.Element;
  footer?: JSX.Element;
  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
}

export const BaseDialog = (props: IBaseDialogProps) => {
  const { styles, title, msg, content, footer, minHeight, maxHeight, ..._fProps } = props;
  const { dialogContentProps, minWidth, modalProps } = props;
  const componentStyles = {
    main: {
      maxHeight,
      minHeight,
    },
  };
  const fProps = {
    ..._fProps,
    dialogContentProps: dialogContentProps || { title },
    modalProps: modalProps || { isBlocking: true },
    minWidth: minWidth || 567,
    styles: useThemeStyles<IDialogStyles>(dialogStylesDefault, [componentStyles, ...singleOrArrayToArray(styles)]),
  };
  return (
    <Dialog {...fProps}>
      {content || msg}
      {footer && <DialogFooter>{footer}</DialogFooter>}
    </Dialog>
  );
};

interface IMessageDialogProps extends IBaseDialogProps {
  dismissalButtonText?: string;
  suppressDismissalButton?: boolean;
}

export const MessageDialog = (props: IMessageDialogProps) => {
  const { onDismiss, dismissalButtonText } = props;
  return (
    <BaseDialog
      {...props}
      footer={
        (!props.suppressDismissalButton && (
          <Button
            styles={[buttonStylesGhost, buttonStylesCenter]}
            onClick={() => onDismiss!()}
            text={dismissalButtonText || 'Ok'}
          />
        )) ||
        undefined
      }
    />
  );
};

interface IDialogProps extends IMessageDialogProps {
  onConfirm: () => unknown;
  confirmationButtonText?: string;
  confirmationButtonDisabled?: boolean;
  confirmationButtonLoading?: boolean;
}

export const ConfirmDialog = (props: IDialogProps) => {
  const {
    onDismiss,
    dismissalButtonText,
    onConfirm,
    confirmationButtonText,
    confirmationButtonDisabled = false,
    confirmationButtonLoading = false,
  } = props;
  return (
    <BaseDialog
      {...props}
      footer={
        <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 10 }}>
          {!props.suppressDismissalButton && (
            <Button styles={buttonStylesGhost} onClick={() => onDismiss!()} text={dismissalButtonText || 'Cancel'} />
          )}
          <Button
            styles={buttonStylesPrimary}
            onClick={onConfirm}
            text={confirmationButtonText || 'Yes'}
            disabled={confirmationButtonDisabled}
            loading={confirmationButtonLoading}
          />
        </Stack>
      }
    />
  );
};
